import { useState, useEffect, useRef } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

import Switch from '~/shared/components/Switch';
import { toaster } from '~/shared/utils/toaster';

import { useToggleRule } from './AutoscaleRuleDetailsSidePanel/hooks/useToggleRule';
import ToggleRuleModal from './AutoscaleRuleDetailsSidePanel/ToggleRuleModal';

import type { AutoscaleRule } from './hooks/useAutoscaleRulesQuery';

interface ToggleRuleRendererProps {
  data: AutoscaleRule;
  isUserReadOnly?: boolean;
}

let isToggleColumnClicked = false;

export function setToggleColumnClicked(value: boolean) {
  isToggleColumnClicked = value;
}

export function isToggleColumnClickedFlag() {
  return isToggleColumnClicked;
}

export default function ToggleRuleRenderer({
  data,
  isUserReadOnly = false,
}: ToggleRuleRendererProps) {
  const [isRowHovered, setIsRowHovered] = useState(false);
  const [isToggleModalOpen, setIsToggleModalOpen] = useState(false);
  const [modalIntentTurningOn, setModalIntentTurningOn] = useState<boolean>(false);
  const cellRef = useRef<HTMLDivElement>(null);
  const switchRef = useRef<HTMLInputElement>(null);

  const { toggleRule, isMutating } = useToggleRule({
    onSuccess: () => {
      setIsToggleModalOpen(false);
    },
    onError: () => {
      setIsToggleModalOpen(false);
    },
  });

  useEffect(() => {
    const cellElement = cellRef.current;
    if (!cellElement) {
      return;
    }

    // Find the parent row element
    const rowElement = cellElement.closest('.ag-row');
    if (!rowElement) {
      return;
    }

    const handleMouseEnter = () => setIsRowHovered(true);
    const handleMouseLeave = () => setIsRowHovered(false);

    rowElement.addEventListener('mouseenter', handleMouseEnter);
    rowElement.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      rowElement.removeEventListener('mouseenter', handleMouseEnter);
      rowElement.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  const handleToggle = () => {
    if (isUserReadOnly) {
      toaster({
        title: 'Action failed',
        message: "You don't have permissions to perform this action",
        variant: 'error',
      });
      return;
    }

    if (isMutating) {
      return;
    }

    const newActiveState = !data.enabled;

    if (newActiveState) {
      toggleRule({
        rule: data,
        newActiveState: true,
      });
    } else {
      setModalIntentTurningOn(newActiveState);
      setIsToggleModalOpen(true);
    }
  };

  const handleToggleConfirm = () => {
    toggleRule({
      rule: data,
      newActiveState: modalIntentTurningOn,
    });
  };

  const handleCloseToggleModal = () => {
    setIsToggleModalOpen(false);
  };

  const handleCellClick = (e: React.MouseEvent) => {
    setToggleColumnClicked(true);

    // Stop propagation to prevent row click when interacting with the cell
    e.stopPropagation();
  };

  return (
    <>
      <div ref={cellRef} className={clsx('flex gap-[18px] items-center')} onClick={handleCellClick}>
        <Switch
          ref={switchRef}
          size="sm"
          checked={data.enabled}
          onChange={handleToggle}
          disabled={isUserReadOnly || isMutating}
        />
        <ChevronDownIcon
          className={clsx(
            'w-[16px] h-[16px] rotate-270 stroke-2 cursor-pointer transition-colors duration-200 ease-in-out',
            isRowHovered ? 'text-primary-500' : 'text-gray-600'
          )}
        />
      </div>

      <ToggleRuleModal
        isOpen={isToggleModalOpen}
        onClose={handleCloseToggleModal}
        onConfirm={handleToggleConfirm}
        isTurningOn={modalIntentTurningOn}
        isPending={isMutating}
      />
    </>
  );
}
